import React, { useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { Loader2, Users, ClipboardList, Target } from "lucide-react";

export default function GlobalSearch({ onNavigate }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ clients: [], consultations: [], leads: [] });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const run = async (v) => {
    setQ(v);
    if (!v || v.length < 2) { setResults({ clients: [], consultations: [], leads: [] }); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/search", { params: { q: v } });
      setResults(data);
    } finally { setLoading(false); }
  };

  const go = (path) => { onNavigate?.(); navigate(path); };

  return (
    <div className="mt-4">
      <Input
        autoFocus
        data-testid="global-search-input"
        placeholder="Search by name, phone, email, nakshatra, rashi, mahadasha…"
        value={q}
        onChange={(e) => run(e.target.value)}
        className="h-12 rounded-xl"
      />
      <div className="mt-4 max-h-[60vh] overflow-y-auto space-y-4">
        {loading && <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Searching…</div>}
        {results.clients?.length > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5"><Users className="w-3 h-3" /> Clients</div>
            <div className="space-y-1">
              {results.clients.map((c) => (
                <button key={c.id} onClick={() => go(`/clients/${c.id}`)} className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-sm" data-testid={`search-client-${c.id}`}>
                  <div className="font-medium">{c.full_name} <span className="font-mono text-xs text-amber-600 ml-2">{c.client_code}</span></div>
                  <div className="text-xs text-slate-500">{c.mobile} • {c.rashi || "—"} • {c.mahadasha || "—"}</div>
                </button>
              ))}
            </div>
          </div>
        )}
        {results.consultations?.length > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5"><ClipboardList className="w-3 h-3" /> Consultations</div>
            {results.consultations.map((c) => (
              <button key={c.id} onClick={() => go(`/consultations`)} className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-sm">
                <div className="font-medium">{c.client_name} — {c.consultation_type}</div>
                <div className="text-xs text-slate-500">{c.date} • {c.status}</div>
              </button>
            ))}
          </div>
        )}
        {results.leads?.length > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5"><Target className="w-3 h-3" /> Leads</div>
            {results.leads.map((l) => (
              <button key={l.id} onClick={() => go(`/leads`)} className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-sm">
                <div className="font-medium">{l.name}</div>
                <div className="text-xs text-slate-500">{l.mobile} • {l.status}</div>
              </button>
            ))}
          </div>
        )}
        {!loading && q.length >= 2 && !results.clients?.length && !results.consultations?.length && !results.leads?.length && (
          <div className="text-sm text-slate-500 text-center py-8">No results for "{q}"</div>
        )}
      </div>
    </div>
  );
}
